import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	OnInit,
	signal,
	ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import {
	CondoUnit,
	DefaultCondoUnit,
	DefaultMatterAdjustmentCommonExpenses,
	DefaultParcelUnit,
	MatterAdjustmentCommonExpenses,
	MatterLRO,
	MatterPropertyType,
	MatterTitle,
	UnitType,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ICON } from '@core/util/icon';
import { cloneDeep } from 'lodash';
import { createId } from '@core/util/file';
import { SalePinState } from '@state/sale_pins';
import { SavedPin } from '@models/teranet';
import { openClose } from '@core/animations/animations';
import { MatterTitlesDispatch } from '@state/matter/titles/titles.dispatch';
import { TestHooks } from './property-units-modal.hooks';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { Logger } from '@core/logger';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { SnackbarService } from '@services/ui/snackbar.service';

const PlanTypes = [
	'Common elements condominium',
	'Standard condominium',
	'Phased condominium plan',
	'Vacant land condominium plan',
	'Leasehold condominium plan',
];

export interface PropertyUnitsModalData {
	title: MatterTitle;
	titleId: string;
}

@UntilDestroy()
@Component({
	selector: 'app-property-units',
	templateUrl: './property-units-modal.component.html',
	styleUrls: ['./property-units-modal.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class PropertyUnitsModalComponent implements OnInit, AfterViewInit {
	@ViewChild('teranetLegalDescContainer') teranetLegalDescContainer: ElementRef;
	@ViewChild('form') ngForm: NgForm;

	testHooks = TestHooks;
	public ICON = ICON;
	public planTypes = PlanTypes;
	public unitTypes = Object.values(UnitType);

	public title: MatterTitle;
	public titleId: string;
	public matchingPinNumber: SavedPin;
	public showTeranetLegalDescriptionPreview = false;
	public editing = true;
	public forceRerender = false;
	public PropertyUsage = PropertyUsage;
	public legalDescTextAreaHeight = signal<string>('auto');
	public showApplyHstCheckbox = signal(false);
	public commonExpensesHstAmt = signal(0);
	public commonExpensesGrandTotal = signal(0);

	public commonExpensesAdjustments = this.store.selectSignal(
		AdjustmentsSelector.commonExpensesAdjustment,
	);

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SalePinState.getParents) parentPins: SavedPin[];
	@SelectSnapshot(SelectedMatter.propertyType) propertyType: MatterPropertyType;
	@SelectSnapshot(SelectedMatter.propertyUsage) propertyUsage: PropertyUsage;
	@ViewSelectSnapshot(SelectedMatter.isCondoUnit) isCondoUnit: boolean;
	@SelectSnapshot(SelectedMatter.LRO) LRO: MatterLRO;
	@SelectSnapshot(SelectedMatter.matterHST) hstPercentage: number;

	constructor(
		@Inject(DRAWER_DATA) private data: PropertyUnitsModalData,
		private matterDispatch: MatterDispatch,
		private matterTitlesDispatch: MatterTitlesDispatch,
		private cdk: ChangeDetectorRef,
		private drawerService: DrawerService,
		private store: Store,
		private adjustmentDispatch: AdjustmentsDispatch,
		private snackbar: SnackbarService,
	) {}

	ngOnInit(): void {
		this.editing = !!this.data?.title;
		this.title = cloneDeep(this.data?.title);

		if (!this.data?.title) {
			this.title = cloneDeep(
				this.isCondoUnit ? DefaultCondoUnit : DefaultParcelUnit,
			);
		}

		if (!this.editing && this.isCondoUnit) {
			this.title.planName = `${this.LRO?.selected?.name ? this.LRO.selected.name + ' ' : ''}Condominium Plan No.`;
		}

		this.titleId = this.data?.titleId ?? createId();
		this.matchingPinNumber = this.parentPins.find(
			(pin) => pin.pin === this.title.pinNumber,
		);

		if (
			this.propertyUsage === PropertyUsage.COMMERCIAL &&
			this.isCondoUnit &&
			!this.editing
		) {
			// Set default value for unit type when adding a new condo unit
			(this.title as CondoUnit).unitType = UnitType.COMMERCIAL_UNIT;
		}

		if (
			(this.title as CondoUnit).unitType === UnitType.COMMERCIAL_UNIT &&
			this.isCondoUnit &&
			!this.editing
		) {
			// Set default value for applyHst when adding a new condo unit
			(this.title as CondoUnit).applyHst = true;
		}

		if ((this.title as CondoUnit).unitType === UnitType.COMMERCIAL_UNIT) {
			this.showApplyHstCheckbox.set(true);
		} else {
			this.showApplyHstCheckbox.set(false);
		}
	}

	ngAfterViewInit(): void {
		// If a Teranet legal description is present, set the height of the editable legal desc text area to match the height of the Teranet legal desc container
		if (
			this.matchingPinNumber &&
			this.teranetLegalDescContainer?.nativeElement?.offsetHeight > 0
		) {
			const teranetLegalDescContainerHeight = (
				this.teranetLegalDescContainer.nativeElement as HTMLDivElement
			).offsetHeight;
			this.legalDescTextAreaHeight.set(`${teranetLegalDescContainerHeight}px`);
		} else {
			this.legalDescTextAreaHeight.set('auto');
		}
	}

	onSubmit(form: NgForm) {
		if (!form.valid) return;

		if (this.isCondoUnit) {
			if ((this.title as CondoUnit).linkedSoAdjCommonExpensesId) {
				this.updateLinkedCommonExpensesAdjustment(
					(this.title as CondoUnit).linkedSoAdjCommonExpensesId,
				);

				this.matterTitlesDispatch.CreateOrUpdate({
					...this.title,
					id: this.titleId,
				});
			} else {
				const newSoAdjCommExpItemId =
					this.createLinkToCommonExpensesAdjustment();

				this.matterTitlesDispatch.CreateOrUpdate({
					...this.title,
					id: this.titleId,
					linkedSoAdjCommonExpensesId: newSoAdjCommExpItemId,
				});
			}
		} else {
			this.matterTitlesDispatch.CreateOrUpdate({
				...this.title,
				id: this.titleId,
			});
		}

		this.drawerService.closeDrawer();
	}

	updateLinkedCommonExpensesAdjustment(linkedSoAdjCommonExpensesId: string) {
		const linkedCommExpAdjustment = this.commonExpensesAdjustments().find(
			(x) => x.id === linkedSoAdjCommonExpensesId,
		);

		if (!linkedCommExpAdjustment) {
			Logger.error(
				`Expected to find SoAdj common expenses item id ${linkedSoAdjCommonExpensesId} but no item matching that id was found. It will be unlinked from title condo unit id ${this.titleId}.`,
			);
			(this.title as CondoUnit).linkedSoAdjCommonExpensesId = null;
			return;
		}

		this.adjustmentDispatch.Set({
			...linkedCommExpAdjustment,
			fees: (this.title as CondoUnit).commonExpenses,
			applyHst:
				(this.title as CondoUnit).unitType === UnitType.COMMERCIAL_UNIT
					? !!(this.title as CondoUnit).applyHst
					: linkedCommExpAdjustment.applyHst,
		} as MatterAdjustmentCommonExpenses);

		this.snackbar.openSnackBar({
			text: 'Common expenses updated and will reflect on statement of adjustments.',
			icon: ICON.CIRCLE_CHECK,
			type: 'success',
		});
	}

	createLinkToCommonExpensesAdjustment(): string {
		const linkableCommExpAdjustment =
			this.findLinkableCommonExpensesAdjustment();

		if (linkableCommExpAdjustment) {
			this.adjustmentDispatch.Set({
				...linkableCommExpAdjustment,
				linkedTitleCondoUnitId: this.titleId,
				fees: (this.title as CondoUnit).commonExpenses,
				applyHst:
					(this.title as CondoUnit).unitType === UnitType.COMMERCIAL_UNIT
						? !!(this.title as CondoUnit).applyHst
						: linkableCommExpAdjustment.applyHst,
			} as MatterAdjustmentCommonExpenses);

			this.snackbar.openSnackBar({
				text: 'Common expenses updated and will reflect on statement of adjustments.',
				icon: ICON.CIRCLE_CHECK,
				type: 'success',
			});

			return linkableCommExpAdjustment.id;
		} else {
			const newCommExpAdjustmentId = createId();

			const newCommExpAdjustmentPayload: MatterAdjustmentCommonExpenses = {
				...structuredClone(DefaultMatterAdjustmentCommonExpenses),
				id: newCommExpAdjustmentId,
				linkedTitleCondoUnitId: this.titleId,
				fees: (this.title as CondoUnit).commonExpenses,
			};

			if ((this.title as CondoUnit).unitType === UnitType.COMMERCIAL_UNIT) {
				newCommExpAdjustmentPayload.applyHst = !!(this.title as CondoUnit)
					.applyHst;
			}

			this.adjustmentDispatch.Set(newCommExpAdjustmentPayload);

			this.snackbar.openSnackBar({
				text: 'Common expenses updated and will reflect on statement of adjustments.',
				icon: ICON.CIRCLE_CHECK,
				type: 'success',
			});

			return newCommExpAdjustmentId;
		}
	}

	findLinkableCommonExpensesAdjustment():
		| MatterAdjustmentCommonExpenses
		| undefined {
		const candidateCommExpAdjustment = this.commonExpensesAdjustments().find(
			(x) => !x.linkedTitleCondoUnitId && x.fees === 0,
		);
		return candidateCommExpAdjustment;
	}

	toggleTeranetLegalDescriptionPreview() {
		this.showTeranetLegalDescriptionPreview =
			!this.showTeranetLegalDescriptionPreview;
	}

	onUnitTypeSelect(unitType: UnitType) {
		if (unitType === UnitType.DWELLING_UNIT) {
			this.title.primary = true;
		} else {
			this.title.primary = false;
		}

		const commonExpenses = (this.title as CondoUnit)?.commonExpenses ?? 0;
		if (unitType === UnitType.COMMERCIAL_UNIT) {
			this.showApplyHstCheckbox.set(true);
			(this.title as CondoUnit).applyHst = true;
			this.calculateCommonExpensesOnHstAmt(true, commonExpenses);
		} else {
			this.showApplyHstCheckbox.set(false);
			(this.title as CondoUnit).applyHst = false;
			this.calculateCommonExpensesOnHstAmt(false, commonExpenses);
		}
	}

	onApplyHstChange(applyHst: boolean) {
		const commonExpenses = (this.title as CondoUnit)?.commonExpenses ?? 0;
		this.calculateCommonExpensesOnHstAmt(applyHst, commonExpenses);
	}

	onCommonExpensesChange(commonExpenses: number) {
		const applyHst = !!(this.title as CondoUnit)?.applyHst;
		this.calculateCommonExpensesOnHstAmt(applyHst, commonExpenses);
	}

	calculateCommonExpensesOnHstAmt(applyHst: boolean, commonExpenses: number) {
		if (applyHst) {
			this.commonExpensesHstAmt.set(
				Math.round(commonExpenses * this.hstPercentage),
			);
			this.commonExpensesGrandTotal.set(
				Math.round(commonExpenses + this.commonExpensesHstAmt()),
			);
		} else {
			this.commonExpensesHstAmt.set(0);
			this.commonExpensesGrandTotal.set(commonExpenses);
		}
	}

	syncLegalDescription() {
		this.title.legalDescription = this.matchingPinNumber.legal_desc;
		this.forceRerender = true;
		this.cdk.detectChanges();
		this.forceRerender = false;
		this.toggleTeranetLegalDescriptionPreview();
	}
}
